import { 
  collection, 
  updateDoc, 
  doc, 
  Timestamp, 
  query, 
  orderBy, 
  limit, 
  onSnapshot,
  writeBatch,
  getDocs,
  DocumentSnapshot,
  startAfter
} from 'firebase/firestore';
import { db } from '../../../auth/firebaseConfig';
import { Message } from '../types';
import { uploadFile } from '../../../utility/firebaseStorageHelpers';

const CONVERSATIONS_COLLECTION = 'conversations';
const MESSAGES_SUBCOLLECTION = 'messages';
export const MESSAGES_PAGE_SIZE = 10;

export const messageService = {
  /**
   * Send a text message
   */
  sendMessage: async (
    conversationId: string, 
    senderId: string, 
    content: string, 
    type: Message['type'] = 'text', 
    metadata?: Message['metadata'],
    recipientIds?: string[]
  ) => {
    const batch = writeBatch(db);
    const now = Timestamp.now();
    
    // 1. Create Message Document
    const messageRef = doc(collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_SUBCOLLECTION));
    const messageData: Omit<Message, 'id'> = {
      senderId,
      content,
      timestamp: now,
      type,
      status: 'sent',
      ...(metadata ? { metadata } : {}),
    };
    batch.set(messageRef, messageData);

    // 2. Update Conversation Last Message & Unread Counts
    const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    
    // Prepare updates
    const updates: any = {
      lastMessage: {
        content: type === 'image' ? 'Sent an image' : content,
        senderId,
        timestamp: now,
        type,
        readBy: [senderId]
      },
      updatedAt: now,
      [`unreadCount.${senderId}`]: 0 // Reset sender's unread count
    };

    // Increment unread count for known recipients
    if (recipientIds && recipientIds.length > 0) {
      // Import dynamically to avoid circular dependency if increment is not auto-imported
      const { increment } = await import('firebase/firestore');
      recipientIds.forEach(id => {
        if (id !== senderId) {
          updates[`unreadCount.${id}`] = increment(1);
        }
      });
    }

    batch.update(conversationRef, updates);

    await batch.commit();
    return messageRef.id;
  },

  /**
   * Send a file/image message
   */
  sendAttachment: async (conversationId: string, senderId: string, file: File) => {
    const path = `conversations/${conversationId}/attachments/${Date.now()}_${file.name}`;
    const url = await uploadFile(file, path);
    
    const type = file.type.startsWith('image/') ? 'image' : 'file';
    
    return messageService.sendMessage(conversationId, senderId, '', type, {
      fileUrl: url,
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size
    });
  },

  /**
   * Subscribe to messages in a conversation
   */
  subscribeToMessages: (conversationId: string, callback: (messages: Message[]) => void) => {
    const q = query(
      collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_SUBCOLLECTION),
      orderBy('timestamp', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Message[];
      callback(messages);
    });
  },

  /**
   * Subscribe to the most recent messages in a conversation (paginated)
   * Returns unsubscribe function
   */
  subscribeToMessagesPaginated: (
    conversationId: string, 
    pageSize: number = MESSAGES_PAGE_SIZE,
    callback: (messages: Message[], firstDoc: DocumentSnapshot | null, hasMore: boolean) => void
  ) => {
    // Query for the most recent messages at bottom of chat (descending order)
    // we re-order them in the callback to display properly
    const q = query(
      collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_SUBCOLLECTION),
      orderBy('timestamp', 'desc'),
      limit(pageSize)
    );

    return onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs;
      // Reverse to get ascending order for display
      const messages = docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).reverse() as Message[];
      
      // The last doc in descending query = oldest fetched = first doc for pagination
      const firstDoc = docs.length > 0 ? docs[docs.length - 1] : null;
      const hasMore = docs.length >= pageSize;
      
      callback(messages, firstDoc, hasMore);
    });
  },

  /**
   * Load older messages before a given document (for infinite scroll up)
   */
  loadOlderMessages: async (
    conversationId: string, 
    beforeDoc: DocumentSnapshot,
    pageSize: number = MESSAGES_PAGE_SIZE
  ): Promise<{ messages: Message[]; firstDoc: DocumentSnapshot | null; hasMore: boolean }> => {
    const q = query(
      collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_SUBCOLLECTION),
      orderBy('timestamp', 'desc'),
      startAfter(beforeDoc),
      limit(pageSize)
    );

    const snapshot = await getDocs(q);
    const docs = snapshot.docs;
    
    // Reverse to get ascending order for display
    const messages = docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).reverse() as Message[];
    
    const firstDoc = docs.length > 0 ? docs[docs.length - 1] : null;
    const hasMore = docs.length >= pageSize;
    
    return { messages, firstDoc, hasMore };
  },
  
  /**
   * Mark message as read (Simplified: simply updates lastMessage readBy for now, 
   * in production ideally tracks individual message state)
   */
  /**
   * Mark conversation as read for a specific user
   */
  markAsRead: async (conversationId: string, userId: string) => {
    const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    try {
      await updateDoc(conversationRef, {
        [`unreadCount.${userId}`]: 0
      });
    } catch (e: any) {
       // Fallback for guest inquiries stored in guestInquiries collection
       if (e.code === 'not-found' || e.code === 'permission-denied') {
          const guestRef = doc(db, 'guestInquiries', conversationId);
          await updateDoc(guestRef, {
            unreadCount: 0
          }).catch(err => console.warn("Could not mark guest inquiry as read", err));
       }
    }
  },

  /**
   * Mark conversation as unread for a specific user
   */
  markAsUnread: async (conversationId: string, userId: string) => {
    const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    try {
      await updateDoc(conversationRef, {
        [`unreadCount.${userId}`]: 1 
      });
    } catch (e: any) {
        if (e.code === 'not-found' || e.code === 'permission-denied') {
          const guestRef = doc(db, 'guestInquiries', conversationId);
          await updateDoc(guestRef, {
            unreadCount: 1
          }).catch(err => console.warn("Could not mark guest inquiry as unread", err));
       }
    }
  }
};
